import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "./AuthContext";
import { getNotifications, markAllRead, markOneRead, markOneUnread, deleteNotification, deleteAllNotifications, markCategoryRead as apiMarkCategoryRead } from "../api/notifications";
import { getConversations, markAllDmRead } from "../api/directMessages";
import API_URL from "../config";

export const NotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  categoryCounts: {},
  dmUnreadCount: 0,
  refresh: () => {},
  markRead: () => {},
  markOne: () => {},
  markCategoryRead: () => {},
  clearDmUnread: () => {},
  subscribeToDirectMessages: () => () => {},
  subscribeToCommunityMessages: () => () => {},
});

export const useNotifications = () => useContext(NotificationContext);

// Maps each notification `type` to the sidebar nav item (or, for
// admin-only types, the specific Admin portal tab) it should badge.
// Single source of truth for this mapping — both the badges and the
// "mark this category read" calls (e.g. opening /jobs, or opening the
// Admin portal's Jobs tab) use it.
//
// admin_jobs / admin_requests / admin_communities are sub-categories of the
// single "Admin" sidebar icon — the sidebar shows one cumulative count
// (ADMIN_SUB_CATEGORIES summed), while inside the Admin portal itself each
// relevant tab shows only its own count, so it's clear *where* an admin
// notification actually belongs instead of one undifferentiated number.
export const ADMIN_SUB_CATEGORIES = ["admin_jobs", "admin_requests", "admin_communities"];

export const TYPE_CATEGORY = {
  new_job: "jobs",
  shortlisted: "jobs",
  new_community: "communities",
  request_accepted: "communities",
  request_rejected: "communities",
  removed_from_community: "communities",
  new_course: "courses",
  course_complete: "courses",
  job_application: "admin_jobs",
  join_request: "admin_requests",
  event_created: "admin_communities",
  event_updated: "admin_communities",
  project_created: "admin_communities",
  project_updated: "admin_communities",
  application_feedback: "messages",
  like: "messages",
  repost: "messages",
  save: "messages",
  comment: "messages",
};

export function categoryOf(type) {
  return TYPE_CATEGORY[type] || "messages";
}

export function typesForCategory(category) {
  return Object.keys(TYPE_CATEGORY).filter((t) => TYPE_CATEGORY[t] === category);
}

export default function NotificationProvider({ children }) {
  const { isAuthenticated } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [dmUnreadCount, setDmUnreadCount] = useState(0);

  const eventSourceRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const dmSubscribersRef = useRef(new Set());
  const communityChatSubscribersRef = useRef(new Set());

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await getNotifications();
      setNotifications(res.data?.notifications || []);
    } catch (e) {
      /* silent — the safety-net poll will try again */
    }
  }, [isAuthenticated]);

  const refreshDmUnread = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await getConversations();
      const total = (res.data?.conversations || []).reduce((sum, c) => sum + (c.unreadCount || 0), 0);
      setDmUnreadCount(total);
    } catch (e) {
      /* silent */
    }
  }, [isAuthenticated]);

  // ---- Single SSE connection for the whole app -----------------------
  // Everything that needs "instant, no refresh" updates (notifications AND
  // direct messages) shares this one connection. Having exactly one
  // connection with one reconnect timer is what prevents the flicker you'd
  // get from multiple independent pollers/streams stepping on each other.
  useEffect(() => {
    if (!isAuthenticated) {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      return;
    }

    let cancelled = false;

    function connect() {
      if (cancelled) return;
      // withCredentials sends the httpOnly auth cookie along with this
      // request — there's no token for JS to put in the URL anymore, which
      // is an improvement in itself (tokens in URLs tend to end up in
      // server/proxy access logs).
      const es = new EventSource(`${API_URL}/notifications/stream`, { withCredentials: true });
      eventSourceRef.current = es;

      es.addEventListener("notification", (event) => {
        try {
          const incoming = JSON.parse(event.data);
          setNotifications((prev) => {
            if (prev.some((n) => n.id === incoming.id)) return prev;
            return [incoming, ...prev];
          });
        } catch (e) {
          /* ignore malformed event */
        }
      });

      es.addEventListener("direct_message", (event) => {
        try {
          const incoming = JSON.parse(event.data);
          // Let anyone showing an open conversation update instantly...
          dmSubscribersRef.current.forEach((cb) => cb(incoming));
          // ...and bump the inbox badge unless that conversation is
          // currently open (the subscriber is responsible for telling us
          // via clearDmUnread when it marks messages read).
          setDmUnreadCount((prev) => prev + 1);
        } catch (e) {
          /* ignore malformed event */
        }
      });

      es.addEventListener("community_chat_message", (event) => {
        try {
          const incoming = JSON.parse(event.data);
          // Same pattern as direct messages — whoever has this community's
          // chat tab open right now gets it appended live. The unread
          // "new activity" badge for community chat is handled separately,
          // through the regular notification channel (see the
          // community_message notification type), not here.
          communityChatSubscribersRef.current.forEach((cb) => cb(incoming));
        } catch (e) {
          /* ignore malformed event */
        }
      });

      es.onerror = () => {
        es.close();
        if (!cancelled) {
          reconnectTimerRef.current = setTimeout(connect, 3000);
        }
      };
    }

    connect();

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimerRef.current);
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };
  }, [isAuthenticated]);

  // ---- Slow polling safety-net -----------------------------------------
  // Only fires every 60s, purely as a fallback in case the SSE connection
  // is blocked by something (corporate proxy, browser extension). Also
  // refreshes on window focus so switching back to the tab catches up
  // immediately instead of waiting for the next tick.
  useEffect(() => {
    refresh();
    refreshDmUnread();
    pollIntervalRef.current = setInterval(() => {
      refresh();
      refreshDmUnread();
    }, 60000);
    return () => clearInterval(pollIntervalRef.current);
  }, [refresh, refreshDmUnread]);

  useEffect(() => {
    const onFocus = () => {
      refresh();
      refreshDmUnread();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh, refreshDmUnread]);

  const markRead = useCallback(async () => {
    try {
      await markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (e) {
      /* silent */
    }
  }, []);

  const markOne = useCallback(async (id) => {
    try {
      await markOneRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch (e) {
      /* silent */
    }
  }, []);

  // The inverse of markOne — lets someone consciously flag a notification
  // as still needing attention, rather than read-state only ever going
  // one direction.
  const markUnread = useCallback(async (id) => {
    try {
      await markOneUnread(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: false } : n)));
    } catch (e) {
      /* silent */
    }
  }, []);

  const deleteOne = useCallback(async (id) => {
    const previous = notifications;
    setNotifications((prev) => prev.filter((n) => n.id !== id)); // optimistic
    try {
      await deleteNotification(id);
    } catch (e) {
      setNotifications(previous); // roll back if the server call actually failed
    }
  }, [notifications]);

  const deleteAll = useCallback(async () => {
    const previous = notifications;
    setNotifications([]); // optimistic
    try {
      await deleteAllNotifications();
    } catch (e) {
      setNotifications(previous);
    }
  }, [notifications]);

  // Marks every notification belonging to a category (e.g. "jobs") as read
  // in one call — this is what makes a sidebar badge disappear the moment
  // its page is opened.
  const markCategoryReadFn = useCallback(async (category) => {
    const types = typesForCategory(category);
    if (types.length === 0) return;
    // Optimistic: clear locally first so the badge disappears instantly,
    // then confirm with the server.
    setNotifications((prev) => prev.map((n) => (types.includes(n.type) ? { ...n, read: true } : n)));
    try {
      await apiMarkCategoryRead(types);
    } catch (e) {
      /* if this fails, the next refresh() will resync the true state */
    }
  }, []);

  // Clears the DM badge — call when the Messages/Inbox page's message tab
  // is opened.
  const clearDmUnread = useCallback(async () => {
    setDmUnreadCount(0);
    try {
      await markAllDmRead();
    } catch (e) {
      /* silent */
    }
  }, []);

  // Lets a component (the open conversation view in Messages.jsx) receive
  // incoming DMs live without opening its own connection or polling.
  const subscribeToDirectMessages = useCallback((callback) => {
    dmSubscribersRef.current.add(callback);
    return () => dmSubscribersRef.current.delete(callback);
  }, []);

  // Same pattern, for whoever has a community's Chat tab open right now
  // (CommunityDetail.jsx) — one shared connection, not a new one per page.
  const subscribeToCommunityMessages = useCallback((callback) => {
    communityChatSubscribersRef.current.add(callback);
    return () => communityChatSubscribersRef.current.delete(callback);
  }, []);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const categoryCounts = useMemo(() => {
    const counts = {
      communities: 0,
      jobs: 0,
      courses: 0,
      messages: 0,
      admin_jobs: 0,
      admin_requests: 0,
      admin_communities: 0,
    };
    notifications.forEach((n) => {
      // direct_message notifications exist so a new DM shows up in the
      // bell/Inbox for visibility, but the Messages sidebar badge is
      // already driven by dmUnreadCount (the actual per-conversation
      // unread count) below — counting both here would double it.
      if (!n.read && n.type !== "direct_message") {
        const cat = categoryOf(n.type);
        counts[cat] = (counts[cat] || 0) + 1;
      }
    });
    // The "messages" nav badge also includes unread direct messages, not
    // just message-category notifications (likes/reposts/saves/feedback).
    counts.messages += dmUnreadCount;
    // The single sidebar "Admin" icon shows one cumulative count across
    // every admin sub-category — the breakdown by tab only happens inside
    // the Admin portal itself.
    counts.admin = ADMIN_SUB_CATEGORIES.reduce((sum, key) => sum + counts[key], 0);
    return counts;
  }, [notifications, dmUnreadCount]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      categoryCounts,
      dmUnreadCount,
      refresh,
      markRead,
      markOne,
      markUnread,
      deleteOne,
      deleteAll,
      markCategoryRead: markCategoryReadFn,
      clearDmUnread,
      subscribeToDirectMessages,
      subscribeToCommunityMessages,
      refreshDmUnread,
    }),
    [
      notifications,
      unreadCount,
      categoryCounts,
      dmUnreadCount,
      refresh,
      markRead,
      markOne,
      markUnread,
      deleteOne,
      deleteAll,
      markCategoryReadFn,
      clearDmUnread,
      subscribeToDirectMessages,
      subscribeToCommunityMessages,
      refreshDmUnread,
    ]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}
